/**
 * Copy for the receivables list (`/stablecoins/invoices`, page spec
 * `invoices-list`, design frame INV-00). One module so a test can assert the
 * page's words without scraping them out of JSX, and so the same sentence is
 * never written twice with two different meanings.
 *
 * Nothing here is a figure. Every count and amount the page shows is read from
 * the contract's response or rendered through the shared unavailable marker —
 * copy states what a state *is*, never what a number *should be*.
 */

export const INVOICES_LIST_COPY = {
  page: {
    title: "Invoices",
    subtitle: "Stablecoin invoices issued to external payers and the payments received against them.",
  },

  /** The five receivable tiles the design frame names (INV-00 summary row). */
  summary: {
    groupLabel: "Receivables summary",
    label: {
      outstanding: "Outstanding",
      overdue: "Overdue",
      confirming: "Confirming",
      underReview: "Under review",
      needsReview: "Needs review",
    },
    /**
     * The outstanding tile's value. The contract has no receivables-total route
     * and the list route carries no aggregate, and a single total would be
     * wrong anyway: one page can mix assets, so a summed figure would add USDC
     * to USDT. The tile therefore shows the shared unavailable marker rather
     * than a computed guess.
     */
    outstandingUnavailable: "Not available",
    outstandingSource: "No receivables total on the invoice list route",
    needsReviewUnavailable: "Not available",
    needsReviewSourceUnavailable: "The exceptions route did not answer",
    needsReviewLoading: "Checking…",
    /** Named source for a tile computed over the loaded page. */
    invoiceSource: "GET /v1/invoices",
    exceptionSource: "GET /v1/invoice-exceptions (open)",
    /**
     * The honest scope note under the row: the tiles are a view over the page
     * the service returned, not an organization-wide roll-up.
     */
    scopeNote:
      "These figures cover the invoices the service has returned so far, not the whole organization.",
    /** Accessible title on the "+" that marks a lower-bound count. */
    atLeast: (count: number) => `At least ${count}; more records are available`,
  },

  filters: {
    searchPlaceholder: "Search number, payer or asset…",
    searchAriaLabel: "Search invoices",
    documentStatusAriaLabel: "Filter by document status",
    paymentStatusAriaLabel: "Filter by payment status",
    allDocumentStatuses: "All document statuses",
    allPaymentStatuses: "All payment statuses",
    /** The search runs over rows already loaded, because the contract has no free-text parameter. */
    searchHint:
      "Search filters the invoices loaded on this page; load more to widen it.",
    chip: { documentStatus: "Document", paymentStatus: "Payment", search: "Search" },
    clearAll: "Clear all",
  },

  table: {
    columns: {
      number: "Invoice",
      payer: "Payer",
      amount: "Amount",
      dueDate: "Due date",
      documentStatus: "Document",
      paymentStatus: "Payment",
    },
    emptyNumber: "—",
    emptyDate: "—",
    paidLate: "Paid late",
    duplicate: "Duplicate",
    duplicatePending: "Duplicating…",
    duplicateAria: (label: string) => `Duplicate invoice ${label}`,
    openAria: (label: string) => `Open invoice ${label}`,
  },

  paging: {
    count: (shown: number, total: number) =>
      `Showing ${shown} of ${total} loaded invoice${total === 1 ? "" : "s"}`,
    moreAvailable: "more available",
    loadMore: "Load more",
    loadingMore: "Loading…",
    loadMoreFailed: "More invoices could not be loaded.",
  },

  create: {
    action: "New invoice",
  },

  export: {
    action: "Export CSV",
    pending: "Queuing export…",
    queued: (exportId: string) =>
      `Export queued (${exportId}). It is produced asynchronously; the download link appears when the job completes.`,
    refusedTitle: "The export was refused",
  },

  duplicate: {
    created: (number: string | null, invoiceId: string) =>
      `Draft created from ${number ?? invoiceId}.`,
    openDraft: "Open the draft",
    refusedTitle: "The invoice could not be duplicated",
  },

  failure: {
    listTitle: "Receivables are not readable right now",
    listDescription:
      "The invoice list route did not answer, so no invoice, total or status is shown. Nothing on this screen is a real figure.",
    deniedTitle: "Access required",
    deniedDescription:
      "Your session is not permitted to read the receivables list. Ask an organization admin to grant the invoice:read scope.",
    deniedRequired: "Required",
    deniedRequiredValue: "invoice:read",
    deniedNeeds: "Needs",
    retry: "Try again",
  },

  empty: {
    title: "No invoices yet",
    description:
      "Invoices issued to external payers appear here, with the payments received against them.",
    filteredTitle: "No invoices match your filters",
    filteredDescription:
      "Nothing in the service's records matches the filters and search you have set.",
    clearFilters: "Clear filters",
  },

  loadingTable: "Loading invoices",
} as const;
